/**
 * Azure AD Configuration Section
 * Allows admins to configure Azure AD settings for their organization
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { saveOrganizationAzureADConfig, getOrganizationAzureADConfig, OrganizationAzureADConfig } from '../../services/WebCloudFunctionsService';
import { Cloud, Save, AlertCircle, CheckCircle, Info, ExternalLink } from 'lucide-react';

interface AzureADConfigSectionProps {
  organizationId: string;
}

const AzureADConfigSection: React.FC<AzureADConfigSectionProps> = ({ organizationId }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [config, setConfig] = useState<OrganizationAzureADConfig>({
    enabled: false,
    clientId: '',
    tenantId: '',
    redirectUri: '',
    scopes: ['openid', 'profile', 'email', 'User.Read'],
  });

  useEffect(() => {
    loadConfig();
  }, [organizationId]);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getOrganizationAzureADConfig(organizationId);
      if (result.azureADConfig) {
        setConfig({
          enabled: result.azureADConfig.enabled || false,
          clientId: result.azureADConfig.clientId || '',
          tenantId: result.azureADConfig.tenantId || '',
          redirectUri: result.azureADConfig.redirectUri || '',
          scopes: result.azureADConfig.scopes || ['openid', 'profile', 'email', 'User.Read'],
        });
      }
    } catch (err: any) {
      console.error('Error loading Azure AD config:', err);
      setError(err.message || 'Failed to load Azure AD configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Validate required fields when enabled
      if (config.enabled) {
        if (!config.clientId || !config.tenantId) {
          setError('Client ID and Tenant ID are required when Azure AD is enabled');
          setSaving(false);
          return;
        }
      }

      // Generate redirect URI if not provided
      const redirectUri = config.redirectUri || `${window.location.origin}/callback`;

      await saveOrganizationAzureADConfig(organizationId, {
        ...config,
        redirectUri,
      });

      setSuccess('Azure AD configuration saved successfully');
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      console.error('Error saving Azure AD config:', err);
      setError(err.message || 'Failed to save Azure AD configuration');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center space-x-3 mb-2">
          <div className="p-2 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg shadow-blue-500/25">
            <Cloud className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 dark:from-white dark:to-slate-200 bg-clip-text text-transparent">
              Azure AD Configuration
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-500 font-medium mt-0.5">
              Configure your organization's Azure AD single sign-on settings
            </p>
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/50 rounded-2xl p-4">
        <div className="flex items-start space-x-3">
          <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm text-blue-900 dark:text-blue-200 font-medium mb-1">
              How to configure Azure AD
            </p>
            <ol className="text-xs text-blue-800 dark:text-blue-300 space-y-1 list-decimal list-inside">
              <li>Register your application in Azure Portal</li>
              <li>Add a redirect URI: <code className="bg-blue-100 dark:bg-blue-900/30 px-1 rounded">{window.location.origin}/callback</code></li>
              <li>Copy the Application (client) ID and Directory (tenant) ID</li>
              <li>Enter them below and save</li>
            </ol>
            <a
              href="https://portal.azure.com/#blade/Microsoft_AAD_IAM/ActiveDirectoryMenuBlade/RegisteredApps"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-1 text-xs text-blue-600 dark:text-blue-400 hover:underline mt-2 font-medium"
            >
              <span>Open Azure Portal</span>
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-xl p-4 flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-800 dark:text-red-200 flex-1">{error}</p>
          <button
            onClick={() => setError(null)}
            className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
          >
            ×
          </button>
        </div>
      )}

      {success && (
        <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/50 rounded-xl p-4 flex items-start space-x-3">
          <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-emerald-800 dark:text-emerald-200 flex-1">{success}</p>
          <button
            onClick={() => setSuccess(null)}
            className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-800 dark:hover:text-emerald-200"
          >
            ×
          </button>
        </div>
      )}

      {/* Configuration Form */}
      <div className="relative overflow-hidden bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl rounded-3xl shadow-2xl shadow-slate-900/10 dark:shadow-black/50 p-8 border border-slate-200/50 dark:border-slate-800/50">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-purple-500/5 to-pink-500/5"></div>
        <div className="relative space-y-6">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700">
            <div>
              <label className="text-sm font-semibold text-slate-900 dark:text-white">
                Enable Azure AD Single Sign-On
              </label>
              <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                Allow users to sign in using Azure AD credentials
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={config.enabled}
                onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-300 dark:bg-slate-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 dark:peer-focus:ring-indigo-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
            </label>
          </div>

          {config.enabled && (
            <>
              {/* Client ID */}
              <div>
                <label className="block text-sm font-semibold text-slate-900 dark:text-white mb-2">
                  Application (Client) ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={config.clientId}
                  onChange={(e) => setConfig({ ...config, clientId: e.target.value })}
                  placeholder="e.g., 078164b8-da30-4c52-aa06-90dbbb98e9f9"
                  className="w-full px-4 py-3 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-xl text-slate-900 dark:text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-transparent transition-all"
                />
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Found in Azure Portal → Your App → Overview → Application (client) ID
                </p>
              </div>

              {/* Tenant ID */}
              <div>
                <label className="block text-sm font-semibold text-slate-900 dark:text-white mb-2">
                  Directory (tenant) ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={config.tenantId}
                  onChange={(e) => setConfig({ ...config, tenantId: e.target.value })}
                  placeholder="e.g., 4ff979c6-f771-4e3a-9aa0-5b7c61c1c691"
                  className="w-full px-4 py-3 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-xl text-slate-900 dark:text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-transparent transition-all"
                />
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Found in Azure Portal → Your App → Overview → Directory (tenant) ID
                </p>
              </div>

              {/* Redirect URI */}
              <div>
                <label className="block text-sm font-semibold text-slate-900 dark:text-white mb-2">
                  Redirect URI
                </label>
                <input
                  type="text"
                  value={config.redirectUri}
                  onChange={(e) => setConfig({ ...config, redirectUri: e.target.value })}
                  placeholder={`${window.location.origin}/callback`}
                  className="w-full px-4 py-3 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-xl text-slate-900 dark:text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-transparent transition-all"
                />
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Default: <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">{window.location.origin}/callback</code>
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Make sure this URI is added in Azure Portal → Your App → Authentication → Redirect URIs
                </p>
              </div>

              {/* Scopes */}
              <div>
                <label className="block text-sm font-semibold text-slate-900 dark:text-white mb-2">
                  API Permissions (Scopes)
                </label>
                <textarea
                  value={config.scopes?.join(', ') || ''}
                  onChange={(e) => setConfig({ ...config, scopes: e.target.value.split(',').map(s => s.trim()).filter(s => s) })}
                  placeholder="openid, profile, email, User.Read"
                  rows={2}
                  className="w-full px-4 py-3 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-xl text-slate-900 dark:text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-transparent transition-all resize-none"
                />
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Comma-separated list of API permissions (default: openid, profile, email, User.Read)
                </p>
              </div>
            </>
          )}

          {/* Save Button */}
          <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full px-6 py-3 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-purple-500/25 transition-all duration-300 hover:scale-[1.02] flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              <Save className="w-5 h-5" />
              <span>{saving ? 'Saving...' : 'Save Configuration'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AzureADConfigSection;


